// CampaignAIBehaviourPanel.jsx
//
// Compact, collapsible panel rendered in the WhatsApp campaign Final Review
// step. Lets the user OPTIONALLY override the company-default AI config for
// THIS campaign by picking from skills + KB docs they have already set up
// in AI Chatbot → My Skills / Knowledge Base.

import React, { useEffect, useState } from 'react';
import {
  Sparkles, Bot, BotOff, Loader2, FileText, ChevronDown, ChevronUp,
} from 'lucide-react';
import { aiConfigApi, kbApi } from '../../../aiChatbot/services/aiChatbotApi';

const CampaignAIBehaviourPanel = ({ value, onChange }) => {
  const [skills, setSkills] = useState([]);
  const [kbDocs, setKbDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  const v = value || { skill_keys: [], kb_tags: [], is_one_way: false };
  const set = (patch) => onChange({ ...v, ...patch });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [skillsRes, docsRes] = await Promise.all([
        kbApi.listSkillDocs().catch(() => null),
        kbApi.list().catch(() => null),
      ]);
      if (cancelled) return;

      const skillList = skillsRes?.data?.entity || skillsRes?.data?.documents || skillsRes?.data?.skills || [];
      setSkills(Array.isArray(skillList) ? skillList : []);

      const docList = docsRes?.data?.entity || docsRes?.data?.documents || [];
      setKbDocs(Array.isArray(docList) ? docList : []);

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleSkill = (key) => {
    const cur = Array.isArray(v.skill_keys) ? v.skill_keys : [];
    set({ skill_keys: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
  };

  // Doc selection writes synthetic `doc:<id>` tags so backend filtering can
  // target individual KB documents.
  const docTagFor = (d) => `doc:${d.id}`;
  const isDocSelected = (d) => {
    const cur = Array.isArray(v.kb_tags) ? v.kb_tags : [];
    return cur.includes(docTagFor(d));
  };
  const toggleDoc = (d) => {
    const tag = docTagFor(d);
    const cur = Array.isArray(v.kb_tags) ? v.kb_tags : [];
    set({ kb_tags: cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag] });
  };

  const skillCount = (v.kb_tags || []).filter((t) => {
    const doc = [...skills].find((s) => `doc:${s.id}` === t);
    return !!doc;
  }).length;
  const docCount = (v.kb_tags || []).filter((t) => {
    const doc = [...kbDocs].find((d) => `doc:${d.id}` === t);
    return !!doc;
  }).length;
  const summary = v.is_one_way
    ? 'Auto-replies disabled'
    : (skillCount === 0 && docCount === 0)
      ? 'Using company defaults'
      : `${skillCount} skill${skillCount === 1 ? '' : 's'}, ${docCount} doc${docCount === 1 ? '' : 's'}`;

  return (
    <div style={{
      marginTop: 16,
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      background: '#fafbff',
    }}>
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={16} color="#4f46e5" />
          <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
            AI behaviour
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>· {summary}</span>
        </div>
        {collapsed ? <ChevronDown size={16} color="#64748b" /> : <ChevronUp size={16} color="#64748b" />}
      </button>

      {!collapsed && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* No-reply toggle */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            padding: '10px 12px', borderRadius: 6,
            background: v.is_one_way ? '#fef2f2' : '#f8fafc',
            cursor: 'pointer',
            border: v.is_one_way ? '1px solid #fecaca' : '1px solid #e2e8f0',
          }}>
            <input
              type="checkbox"
              checked={!!v.is_one_way}
              onChange={(e) => set({ is_one_way: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {v.is_one_way ? <BotOff size={14} color="#dc2626" /> : <Bot size={14} color="#16a34a" />}
                One-way campaign (no auto-reply)
              </div>
            </div>
          </label>

          {!v.is_one_way && (
            <>
              {/* Skills */}
              <Section title="Skills" hint="Pick one or more">
                {loading ? (
                  <Loading text="Loading skills…" />
                ) : skills.length === 0 ? (
                  <Empty text="No skills yet · add some in AI Chatbot → My Skills" />
                ) : (
                  <Chips>
                    {skills.map((s) => {
                      const tag = `doc:${s.id}`;
                      if (!s.id) return null;
                      const active = (v.kb_tags || []).includes(tag);
                      return (
                        <Chip
                          key={s.id}
                          active={active}
                          activeColor="#4f46e5"
                          activeBg="#eef2ff"
                          activeText="#4338ca"
                          onClick={() => toggleDoc(s)}
                          title={s.title || ''}
                        >
                          {s.title || s.id}
                        </Chip>
                      );
                    })}
                  </Chips>
                )}
              </Section>

              {/* KB docs */}
              <Section title="Knowledge base" hint="Pick docs (optional)">
                {loading ? (
                  <Loading text="Loading documents…" />
                ) : kbDocs.length === 0 ? (
                  <Empty text="No documents yet · add some in AI Chatbot → Knowledge Base" />
                ) : (
                  <Chips>
                    {kbDocs.map((d) => {
                      const active = isDocSelected(d);
                      return (
                        <Chip
                          key={d.id}
                          active={active}
                          activeColor="#0891b2"
                          activeBg="#ecfeff"
                          activeText="#0e7490"
                          onClick={() => toggleDoc(d)}
                          title={d.title}
                          icon={<FileText size={11} />}
                        >
                          {d.title}
                        </Chip>
                      );
                    })}
                  </Chips>
                )}
              </Section>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ──────────────── small presentational helpers ──────────────── */

const Section = ({ title, hint, children }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#334155' }}>
      {title}
      {hint && (
        <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
          {hint}
        </span>
      )}
    </div>
    {children}
  </div>
);

const Loading = ({ text }) => (
  <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
    <Loader2 size={12} className="ai-spin" /> {text}
  </div>
);

const Empty = ({ text }) => (
  <div style={{
    fontSize: 12, color: '#64748b',
    background: '#f8fafc', border: '1px dashed #cbd5e1',
    borderRadius: 6, padding: 8,
  }}>
    {text}
  </div>
);

const Chips = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
);

const Chip = ({ active, activeColor, activeBg, activeText, onClick, title, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      fontSize: 12,
      padding: '4px 10px',
      borderRadius: 999,
      border: active ? `1px solid ${activeColor}` : '1px solid #e2e8f0',
      background: active ? activeBg : '#fff',
      color: active ? activeText : '#475569',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      maxWidth: 240,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {icon}
    {children}
  </button>
);

export default CampaignAIBehaviourPanel;
